import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef
} from "react";
import {
  Environment,
  SystemOfRecord
} from "@coxauto-ui/cxm/communications/interfaces";
import { getTextOptInStatus } from "@coxauto-ui/cxm/communications/api";

// Match the conversation polling interval
const OPT_IN_POLL_INTERVAL_MS = 15000;

interface UseTextOptInStatusParams {
  env: Environment;
  customerId: string;
  customerSystem: SystemOfRecord;
  selectedToChannelValue: string;
  /** From ContactDetail.consent.canSend — false means already opted out, skip poll */
  canSend: boolean;
  /** From ContactDetail.consent.optInStatus — null means unknown, poll; true/false means resolved, skip poll */
  optInStatus: boolean | null;
  /** From ContactDetail.consent.ebrStatus — true means existing business relationship, skip poll regardless of optInStatus */
  ebrStatus: boolean | null;
  setIsTextOptInPending: Dispatch<SetStateAction<boolean>>;
  /** Called when opt-in transitions from pending → resolved so contact details can be refreshed */
  refreshContactDetails: () => void;
}

export function useTextOptInStatus({
  env,
  customerId,
  customerSystem,
  selectedToChannelValue,
  canSend,
  optInStatus,
  ebrStatus,
  setIsTextOptInPending,
  refreshContactDetails
}: UseTextOptInStatusParams) {
  const pollingTimer = useRef<number>(0);
  const wasPendingRef = useRef<boolean>(false);

  const shouldPoll =
    !!selectedToChannelValue && canSend && optInStatus === null && !ebrStatus;

  const checkOptInStatus = useCallback(async () => {
    if (!shouldPoll) {
      setIsTextOptInPending(false);
      return;
    }

    try {
      const response = await getTextOptInStatus(
        env,
        customerId,
        customerSystem,
        selectedToChannelValue
      );

      const isPending = response.isOptInPending;

      // Detect pending → resolved transition and refresh contact details
      if (wasPendingRef.current && !isPending) {
        refreshContactDetails();
      }

      wasPendingRef.current = isPending;
      setIsTextOptInPending(isPending);
    } catch {
      // On error, don't block the user
      wasPendingRef.current = false;
      setIsTextOptInPending(false);
    }
  }, [
    shouldPoll,
    env,
    customerId,
    customerSystem,
    selectedToChannelValue,
    setIsTextOptInPending,
    refreshContactDetails
  ]);

  useEffect(() => {
    // Reset pending tracking ref when the selected number changes
    wasPendingRef.current = false;

    checkOptInStatus();

    if (pollingTimer.current) {
      clearInterval(pollingTimer.current);
    }

    if (shouldPoll) {
      pollingTimer.current = window.setInterval(
        checkOptInStatus,
        OPT_IN_POLL_INTERVAL_MS
      );
    }

    return () => {
      clearInterval(pollingTimer.current);
    };
  }, [checkOptInStatus, shouldPoll]);
}
